import { AppShell } from '../components/AppShell';
import { BRAND } from '../config/brand';
import { PRIVACY_VERSION, PRIVACY_LAST_UPDATED } from '../data/legal';

/**
 * In-app Privacy Policy.
 *
 * Mirrors the canonical policy hosted at https://safeandsorted.com/privacy.html.
 * Source of truth is 4.0 Legals / 4.2 Privacy and Terms / PRIVACY_POLICY.md.
 *
 * Keep the two in sync — update both when the policy changes, and bump
 * PRIVACY_VERSION + PRIVACY_LAST_UPDATED in data/legal.ts.
 */
export function Privacy() {
  return (
    <AppShell title="Privacy policy" back="/settings">
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 13 }}>
          Version {PRIVACY_VERSION} · Last updated {PRIVACY_LAST_UPDATED}.
        </p>
        <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', fontSize: 13 }}>
          Plain-English summary in the app. The full canonical version lives at{' '}
          <a href="https://safeandsorted.com/privacy.html" target="_blank" rel="noreferrer">
            safeandsorted.com/privacy
          </a>{' '}
          and forms part of your subscription agreement.
        </p>
      </div>

      <h3 className="section-title">Who handles your data</h3>
      <div className="card">
        <p>
          {BRAND.name} is operated by <strong>{BRAND.legalEntity}</strong> (company no. {BRAND.companyNumber}),
          registered in {BRAND.companyJurisdiction}. Registered office: {BRAND.registeredOffice}.
        </p>
        <p style={{ marginBottom: 0 }}>
          We&apos;re the <strong>data controller</strong> for your account data (you, the person who signed up).
          For the people, subbies and visitors <em>you</em> enter into the app, your business is the
          controller and we&apos;re the processor — we just store and process it on your behalf.
        </p>
      </div>

      <h3 className="section-title">What we collect</h3>
      <div className="card">
        <p><strong>About you:</strong> name, email, phone, your role, the trades you select, encrypted password, billing address, IP at login, device type, the actions you take in the app.</p>
        <p><strong>About people you enter:</strong> employee names, training dates, signatures; sub-contractor company details and insurance expiry dates; site visitor names, mobiles, vehicle regs and sign-in / sign-out times.</p>
        <p style={{ marginBottom: 0 }}><strong>What we don&apos;t collect:</strong> your card number (that lives with Stripe), nothing from third-party trackers, nothing for advertising. We do not run third-party analytics at launch.</p>
      </div>

      <h3 className="section-title">Why — and the legal basis for it</h3>
      <div className="card">
        <p><strong>Running your account.</strong> Performance of contract.</p>
        <p><strong>Operational emails</strong> (expiry alerts, password resets, invoices). Performance of contract.</p>
        <p><strong>Marketing emails.</strong> Consent — withdraw at any time using the unsubscribe link in every marketing email.</p>
        <p><strong>HSE / RIDDOR / CDM record-keeping.</strong> Legal obligation — yours, processed by us on your behalf.</p>
        <p style={{ marginBottom: 0 }}><strong>Billing, fraud prevention, tax.</strong> Legal obligation and contract.</p>
      </div>

      <h3 className="section-title">Who else sees it</h3>
      <div className="card">
        <p>Only the sub-processors that operate the Service on our behalf:</p>
        <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
          <li><strong>Supabase</strong> — database, auth, file storage. Europe (Frankfurt).</li>
          <li><strong>Stripe</strong> — payment processing. UK / EU.</li>
          <li><strong>Resend</strong> — email delivery. EU / US (SCCs in place).</li>
          <li><strong>Netlify</strong> — web hosting. EU-region origin.</li>
          <li><strong>Apple App Store / Google Play</strong> — only if you download the native app.</li>
          <li><strong>Tapfiliate</strong> — affiliate-programme attribution, only when activated. Netherlands (EU).</li>
        </ul>
        <p style={{ marginBottom: 0 }}>Plus regulators (HMRC, ICO, HSE, police on a valid order) and our professional advisors under confidentiality. <strong>We do not sell personal data and we do not use it for advertising.</strong></p>
      </div>

      <h3 className="section-title">Where it lives</h3>
      <div className="card">
        <p style={{ marginBottom: 0 }}>
          On UK / European servers (Supabase, Frankfurt region). The one sub-processor based outside the UK/EEA
          (Resend) operates under UK International Data Transfer Addendum SCCs.
        </p>
      </div>

      <h3 className="section-title">How long we keep it</h3>
      <div className="card">
        <p>Active accounts — for the life of your subscription, plus 30 days.</p>
        <p>Billing records — 7 years (HMRC).</p>
        <p>Site sign-ins — 6 years (personal-injury claim limitation), or as you configure.</p>
        <p>Accident / RIDDOR records — at least 3 years from injury, or until the injured person turns 21 if a minor.</p>
        <p style={{ marginBottom: 0 }}>Backups — overwritten within 90 days.</p>
      </div>

      <h3 className="section-title">Your rights</h3>
      <div className="card">
        <p>Under UK GDPR you can ask us to:</p>
        <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
          <li>Show you the data we hold about you (access)</li>
          <li>Correct anything wrong (rectification)</li>
          <li>Delete it where the law allows (erasure)</li>
          <li>Restrict how we process it</li>
          <li>Export it in a machine-readable format (portability)</li>
          <li>Object to processing based on legitimate interest</li>
          <li>Withdraw consent (e.g. marketing emails)</li>
        </ul>
        <p style={{ marginBottom: 6 }}>
          Email <a href="mailto:privacy@safeandsorted.com">privacy@safeandsorted.com</a> with proof of identity.
          We respond within 30 days.
        </p>
        <p style={{ marginBottom: 0, color: 'var(--ink-soft)', fontSize: 13 }}>
          Not happy with our response? You can complain to the Information Commissioner&apos;s Office (ICO):
          ico.org.uk/make-a-complaint · 0303 123 1113.
        </p>
      </div>

      <h3 className="section-title">Cookies &amp; storage on your device</h3>
      <div className="card">
        <p>
          <strong>Authentication cookies</strong> to keep you signed in.{' '}
          <strong>localStorage / IndexedDB</strong> for your preferences, your active site, and the offline outbox
          (so the app keeps working without signal).
        </p>
        <p style={{ marginBottom: 0 }}>
          No cross-site tracking, no fingerprinting, no advertising cookies, no third-party analytics at launch.
        </p>
      </div>

      <h3 className="section-title">Security</h3>
      <div className="card">
        <p style={{ marginBottom: 0 }}>
          TLS 1.2+ in transit and encryption at rest. Role-based access with least privilege.
          Multi-factor auth for all our staff with production access. Daily encrypted backups.
          Audit logs for sensitive actions. Concurrent-session limits to detect account sharing.
          If you think your account has been compromised, email{' '}
          <a href="mailto:privacy@safeandsorted.com">privacy@safeandsorted.com</a> immediately.
        </p>
      </div>

      <h3 className="section-title">Children</h3>
      <div className="card">
        <p style={{ marginBottom: 0 }}>
          The Service is for adults in a workplace. We don&apos;t knowingly collect data from anyone under 16.
          Apprentices under 18 may appear in site sign-in records for safety record-keeping only — no marketing
          or analytics.
        </p>
      </div>

      <h3 className="section-title">Contact</h3>
      <div className="card">
        <p style={{ marginBottom: 6 }}>
          <strong>Email:</strong> <a href="mailto:privacy@safeandsorted.com">privacy@safeandsorted.com</a>
        </p>
        <p style={{ marginBottom: 6 }}>
          <strong>Post:</strong> Data Protection, {BRAND.legalEntity}, {BRAND.registeredOffice}.
        </p>
        <p style={{ marginBottom: 0, color: 'var(--ink-soft)', fontSize: 13 }}>
          The full canonical policy with the sub-processor list, retention table and Data Processing Terms lives at{' '}
          <a href="https://safeandsorted.com/privacy.html" target="_blank" rel="noreferrer">
            safeandsorted.com/privacy
          </a>.
        </p>
      </div>
    </AppShell>
  );
}
